import React, { useState } from "react";
import { CaretLeft, ShieldCheck, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  hasPin,
  setPin as savePin,
  clearPin,
  verifyPin,
  isValidPin,
} from "../../lib/vaultLock";

/**
 * Settings → Vault security
 *
 * Three modes:
 *   • root: shows current status (PIN on / off) with toggle + change button
 *   • create: prompts for new PIN twice (confirm)
 *   • change: requires current PIN, then new PIN twice
 *
 * Pure client-side; PIN is hashed and stored in localStorage.
 */
export default function VaultPinManager({ onBack }) {
  const [pinOn, setPinOn] = useState(hasPin());
  const [mode, setMode] = useState("root"); // root | create | change

  function handleCleared() {
    clearPin();
    setPinOn(false);
    setMode("root");
    toast.success("Vault PIN removed");
  }

  return (
    <div className="px-5 pt-4 pb-28" data-testid="vault-security-screen">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => (mode === "root" ? onBack() : setMode("root"))}
          aria-label="Back"
          data-testid="vault-security-back"
          className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center"
        >
          <CaretLeft size={20} weight="bold" color="#0E2C54" />
        </button>
        <h2 className="font-jakarta font-extrabold text-navy text-xl flex items-center gap-2">
          <ShieldCheck size={22} weight="duotone" color="#0E2C54" />
          Vault security
        </h2>
        <div className="w-10 h-10" />
      </div>

      {mode === "root" && (
        <>
          <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-jakarta font-extrabold text-navy text-base">
                  4-digit PIN
                </div>
                <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5">
                  {pinOn
                    ? "Vault is locked until you enter your PIN."
                    : "Add an extra layer over your IDs, family, and travel docs."}
                </div>
              </div>
              <Toggle
                value={pinOn}
                onChange={(next) => {
                  if (next) setMode("create");
                  else if (pinOn) setMode("change"); // require current PIN before removal
                }}
                testId="vault-pin-toggle"
              />
            </div>
          </div>

          {pinOn && (
            <>
              <button
                type="button"
                data-testid="vault-pin-change-btn"
                onClick={() => setMode("change")}
                className="w-full bg-navy text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft"
              >
                Change PIN
              </button>
              <button
                type="button"
                data-testid="vault-pin-remove-btn"
                onClick={() => setMode("change")}
                className="w-full mt-3 bg-white border border-navy/10 rounded-full py-3.5 flex items-center justify-center gap-2 text-orange font-jakarta font-bold"
              >
                <Trash size={18} weight="duotone" /> Remove PIN
              </button>
            </>
          )}

          <p className="text-navy-soft text-xs font-jakarta font-medium mt-6 text-center leading-relaxed max-w-[34ch] mx-auto">
            The PIN is hashed and stored only on this device. If you forget it,
            you can clear the app data to reset.
          </p>
        </>
      )}

      {mode === "create" && (
        <CreatePinForm
          onSaved={() => {
            setPinOn(true);
            setMode("root");
            toast.success("Vault PIN set");
          }}
          onCancel={() => setMode("root")}
        />
      )}

      {mode === "change" && (
        <ChangePinForm
          onChanged={() => {
            setPinOn(true);
            setMode("root");
            toast.success("Vault PIN updated");
          }}
          onCleared={handleCleared}
          onCancel={() => setMode("root")}
        />
      )}
    </div>
  );
}

function Toggle({ value, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      data-testid={testId}
      onClick={() => onChange(!value)}
      className={`shrink-0 w-12 h-7 rounded-full relative transition-colors ${
        value ? "bg-orange" : "bg-navy/15"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-soft transition-transform ${
          value ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function PinInput({ value, onChange, testId, autoFocus }) {
  return (
    <input
      inputMode="numeric"
      pattern="\d*"
      maxLength={4}
      autoFocus={autoFocus}
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
      className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-3 text-2xl font-jakarta font-extrabold text-navy text-center tracking-[0.6em] outline-none focus:border-orange/40"
      placeholder="••••"
    />
  );
}

function CreatePinForm({ onSaved, onCancel }) {
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (!isValidPin(pin1)) {
      toast.error("Enter a 4-digit PIN");
      return;
    }
    if (pin1 !== pin2) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await savePin(pin1);
      onSaved();
    } catch {
      toast.error("Couldn't save PIN");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-2xl shadow-soft border border-navy/10 p-5 space-y-4"
      data-testid="vault-pin-create-form"
    >
      <div>
        <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
          New PIN
        </div>
        <PinInput value={pin1} onChange={setPin1} testId="vault-pin-new" autoFocus />
      </div>
      <div>
        <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
          Confirm PIN
        </div>
        <PinInput value={pin2} onChange={setPin2} testId="vault-pin-confirm" />
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid="vault-pin-save-btn"
          className="flex-1 bg-orange text-white rounded-full py-3 font-jakarta font-extrabold disabled:opacity-60"
        >
          Save PIN
        </button>
      </div>
    </form>
  );
}

function ChangePinForm({ onChanged, onCleared, onCancel }) {
  const [current, setCurrent] = useState("");
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [currentOk, setCurrentOk] = useState(false);

  async function verifyCurrent() {
    if (!isValidPin(current)) return;
    const ok = await verifyPin(current);
    if (ok) {
      setCurrentOk(true);
    } else {
      toast.error("Incorrect current PIN");
      setCurrent("");
    }
  }

  async function submitChange(e) {
    e?.preventDefault?.();
    if (!currentOk) {
      await verifyCurrent();
      return;
    }
    if (!isValidPin(pin1)) {
      toast.error("Enter a 4-digit PIN");
      return;
    }
    if (pin1 !== pin2) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await savePin(pin1);
      onChanged();
    } catch {
      toast.error("Couldn't save PIN");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submitChange}
      className="bg-white rounded-2xl shadow-soft border border-navy/10 p-5 space-y-4"
      data-testid="vault-pin-change-form"
    >
      <div>
        <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
          Current PIN
        </div>
        <PinInput
          value={current}
          onChange={(v) => {
            setCurrent(v);
            setCurrentOk(false);
          }}
          testId="vault-pin-current"
          autoFocus
        />
      </div>
      {!currentOk && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={verifyCurrent}
            data-testid="vault-pin-verify-btn"
            className="flex-1 bg-navy text-white rounded-full py-3 font-jakarta font-extrabold"
          >
            Continue
          </button>
        </div>
      )}
      {currentOk && (
        <>
          <div>
            <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
              New PIN
            </div>
            <PinInput value={pin1} onChange={setPin1} testId="vault-pin-new" autoFocus />
          </div>
          <div>
            <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
              Confirm new PIN
            </div>
            <PinInput value={pin2} onChange={setPin2} testId="vault-pin-confirm" />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCleared}
              data-testid="vault-pin-remove-confirm-btn"
              className="flex-1 bg-white border border-orange/30 rounded-full py-3 font-jakarta font-bold text-orange"
            >
              Remove PIN
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="vault-pin-save-btn"
              className="flex-1 bg-orange text-white rounded-full py-3 font-jakarta font-extrabold disabled:opacity-60"
            >
              Save new PIN
            </button>
          </div>
        </>
      )}
    </form>
  );
}
